import { useChat } from "@ai-sdk/react";
import { ApolloError, useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { captureException } from "@sentry/react";
import {
  DefaultChatTransport,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
} from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEpocusClaimRefresh } from "../../_lib/rbac/epocus-claim-refresh";
import { useAgentUrl } from "./dev-agent-url";
import { MockChatTransport } from "./mock-chat-transport";
import { findScenario, withViewerName } from "./mock-script";
import type { OnboardingUIMessage, WatchOutcome } from "./onboarding-chat";
import {
  createApolloRoutingWatcher,
  createMockRoutingWatcher,
} from "./routing-watcher";

const ROUTING_POLL_INTERVAL_MS = 3000;
const ELAPSED_TICK_MS = 1000;

const PROVISIONING_TOOLS = new Set([
  "createEpocusProgram",
  "provisionEpocusProgramMember",
  "addEpocusProgramMember",
  "updateEpocusUser",
  "submit",
]);

const MEMBERSHIP_QUERY = "EpocusMyProgramMembership";

type Part = OnboardingUIMessage["parts"][number];

const landedProvisioningCallId = (part: Part): string | undefined => {
  if (part.type === "dynamic-tool") {
    return part.state === "output-available" &&
      PROVISIONING_TOOLS.has(part.toolName)
      ? part.toolCallId
      : undefined;
  }
  if (!isToolUIPart(part)) return undefined;
  return part.state === "output-available" &&
    PROVISIONING_TOOLS.has(part.type.slice("tool-".length))
    ? part.toolCallId
    : undefined;
};

const failureMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  return error instanceof ApolloError && error.graphQLErrors.length > 0
    ? `was refused by the server (${message})`
    : `could not reach the server (${message})`;
};

const sessionIdFor = (subject: string | undefined): string | undefined =>
  subject ? `onboarding:${subject}` : undefined;

export type WatchProgress = {
  attempt: number;
  elapsedMs: number;
  outcome?: WatchOutcome;
  detail?: string;
  failure?: string;
};

export const useOnboardingChat = (scenarioId: string) => {
  const agentUrl = useAgentUrl();
  const { user, getAccessTokenSilently } = useAuth0();
  // auth0-react returns a fresh function identity each render; depending on it
  // rebuilds the transport, which restarts the conversation.
  const getToken = useRef(getAccessTokenSilently);
  getToken.current = getAccessTokenSilently;
  const refreshEpocusClaim = useEpocusClaimRefresh();
  const refreshClaim = useRef(refreshEpocusClaim);
  refreshClaim.current = refreshEpocusClaim;

  const transport = useMemo(
    () =>
      agentUrl
        ? new DefaultChatTransport<OnboardingUIMessage>({
            api: `${agentUrl.replace(/\/$/, "")}/chat`,
            // api-imaging-study's `epocusOnboardingCaller` resolves the admin from
            // this header specifically; an `authorization` header reaches the agent
            // as nobody.
            headers: async () => ({
              "x-epocus-on-behalf-of-token": await getToken.current(),
            }),
          })
        : new MockChatTransport(
            withViewerName(findScenario(scenarioId), user?.name),
          ),
    [agentUrl, scenarioId, user?.name],
  );

  const chat = useChat<OnboardingUIMessage>({
    id: sessionIdFor(user?.sub),
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
  });

  const { sendMessage, status, messages } = chat;

  // Seeding the greeting with `setMessages` instead diverges the SDK's internal
  // state, which then appends a snapshot of the message on every update.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    void sendMessage({
      role: "user",
      parts: [{ type: "data-open", data: {} }],
    });
  }, [sendMessage]);

  const apolloClient = useApolloClient();

  const provisionedCallIds = useRef(new Set<string>());
  useEffect(() => {
    const landed = messages
      .flatMap((message) => message.parts)
      .map(landedProvisioningCallId)
      .filter(
        (callId): callId is string =>
          callId !== undefined && !provisionedCallIds.current.has(callId),
      );
    if (landed.length === 0) return;
    for (const callId of landed) provisionedCallIds.current.add(callId);
    void refreshClaim.current().catch(captureException);
    void apolloClient
      .refetchQueries({ include: [MEMBERSHIP_QUERY] })
      .catch(captureException);
  }, [messages, apolloClient]);

  const watcher = useMemo(
    () =>
      transport instanceof MockChatTransport
        ? createMockRoutingWatcher(transport.watchPlan)
        : createApolloRoutingWatcher(apolloClient),
    [transport, apolloClient],
  );

  const [watchProgress, setWatchProgress] = useState<
    Record<string, WatchProgress>
  >({});
  const checkNowRef = useRef<() => void>();

  const pendingWatch = messages
    .flatMap((message) => message.parts)
    .find(
      (part) =>
        part.type === "data-watch" && !watchProgress[part.id ?? ""]?.outcome,
    );
  const watchId =
    pendingWatch?.type === "data-watch" ? pendingWatch.id : undefined;
  const watchData =
    pendingWatch?.type === "data-watch" ? pendingWatch.data : undefined;
  const watchProgramId = watchData?.programId;
  const watchSince = watchData?.since;

  useEffect(() => {
    if (!watchId || !watchProgramId || !watchSince || status === "streaming")
      return;

    const controller = new AbortController();
    const startedAt = performance.now();
    let attempt = 0;
    let completed = 0;
    let polling = false;
    let settled = false;

    const record = (progress: Partial<WatchProgress>) =>
      setWatchProgress((current) => {
        const previous = current[watchId] ?? {
          attempt: completed,
          elapsedMs: performance.now() - startedAt,
        };
        return { ...current, [watchId]: { ...previous, ...progress } };
      });

    const poll = async () => {
      if (polling || settled || controller.signal.aborted) return;
      polling = true;
      attempt += 1;
      try {
        const elapsedMs = performance.now() - startedAt;
        const observation = await watcher.poll({
          programId: watchProgramId,
          since: watchSince,
          attempt,
          elapsedMs,
          signal: controller.signal,
        });
        completed += 1;
        record({
          attempt: completed,
          elapsedMs,
          failure: undefined,
          ...observation,
        });
        if (!observation.outcome) return;
        settled = true;
        void sendMessage({
          role: "user",
          parts: [
            {
              type: "data-watch-outcome",
              data: {
                outcome: observation.outcome,
                detail: observation.detail,
              },
            },
          ],
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        record({ failure: failureMessage(error) });
      } finally {
        polling = false;
      }
    };

    checkNowRef.current = () => void poll();
    const pollTimer = setInterval(() => void poll(), ROUTING_POLL_INTERVAL_MS);
    const elapsedTimer = setInterval(() => {
      if (settled) return;
      record({ elapsedMs: performance.now() - startedAt });
    }, ELAPSED_TICK_MS);

    return () => {
      clearInterval(pollTimer);
      clearInterval(elapsedTimer);
      checkNowRef.current = undefined;
      controller.abort();
    };
  }, [watchId, watchProgramId, watchSince, status, watcher, sendMessage]);

  const checkRoutingNow = useCallback(() => checkNowRef.current?.(), []);

  const progress = useMemo(
    () =>
      messages
        .flatMap((message) => message.parts)
        .reduce<{ step: number; total: number } | undefined>(
          (latest, part) =>
            part.type === "data-progress" ? part.data : latest,
          undefined,
        ),
    [messages],
  );

  const lastMessage = messages.at(-1);
  const textInput = useMemo(
    () =>
      lastMessage?.role === "assistant"
        ? lastMessage.parts.find((part) => part.type === "data-text-input")
            ?.data
        : undefined,
    [lastMessage],
  );

  return { ...chat, watchProgress, checkRoutingNow, progress, textInput };
};
